import { memo, useMemo } from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig } from 'remotion';
import { getCaptionPreset } from '@goldcast/player-config/src/caption-presets/utils';
import { CaptionPresetName } from '@goldcast/player-config/src/caption-presets/types';
import { WordType, type Word } from '@goldcast/player-config/src/types/transcript-types';
import { type CaptionStyles, SizeType, CaptionLineConfig } from '@goldcast/player-config/src/types/asset-types';
import { SegmentType } from '@goldcast/player-config/src/types/Segment/Type';
import { CaptionTrackMetadata } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Track/CaptionsTrack/CaptionsTrack';
import { FONT_DEFAULT_VALUE } from '@goldcast/player-config/src/constants/fonts';
import { getEnvConfig } from '@/constants';
import { generateCaptionPages } from '@/App/remotion/libs/caption/generate-caption-pages';
import CaptionsPages from '@/App/remotion/components/Captions/CaptionsPages';
import { getFontFamilyStyle } from '@/App/remotion/libs/font';

interface CaptionPresetPreviewCompositionProps {
  presetName: CaptionPresetName;
  isHovered?: boolean;
}

const CAPTION_PREVIEW_TEXT = "Welcome to our amazing product demo! Let's explore the awesome features";
const WORD_DURATION_SECONDS = 0.5;

function getPreviewFontSize(preset: Partial<CaptionStyles>): number {
  switch (preset.line_config) {
    case CaptionLineConfig.OneWord:
      return 180;

    case CaptionLineConfig.OneLine:
      return 140;

    case CaptionLineConfig.TwoLines:
    case CaptionLineConfig.ThreeLines:
      return 120;

    default:
      return 72;
  }
}

function getPreviewCaptionPosition(preset: Partial<CaptionStyles>): any {
  switch (preset.line_config) {
    case CaptionLineConfig.OneWord:
      return {
        x: '5%',
        y: '30%',
        width: '90%',
        height: '20%'
      };

    case CaptionLineConfig.OneLine:
      return {
        x: '5%',
        y: '33%',
        width: '90%',
        height: '20%'
      };

    case CaptionLineConfig.TwoLines:
    case CaptionLineConfig.ThreeLines:
      return {
        x: '5%',
        y: '25%',
        width: '90%',
        height: '20%'
      };

    default:
      return {
        x: '5%',
        y: '25%',
        width: '90%',
        height: '20%'
      };
  }
}

function CaptionPresetPreviewComposition({ presetName, isHovered = false }: CaptionPresetPreviewCompositionProps) {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const dummyWords = useMemo((): Word[] => {
    return CAPTION_PREVIEW_TEXT.split(' ').map((word, index) => ({
      id: `preview-word-${index}`,
      content: word,
      processed_start_time: index * WORD_DURATION_SECONDS,
      processed_end_time: (index + 1) * WORD_DURATION_SECONDS,
      srt_index: Math.floor(index / 6) + 1,
      start_time: index * WORD_DURATION_SECONDS,
      end_time: (index + 1) * WORD_DURATION_SECONDS,
      confidence: 1.0,
      speaker: 0,
      hidden: false,
      manual_edit: false,
      type: WordType.Pronunciation,
      speaker_label: 'Speaker',
      index: index
    }));
  }, []);

  const metadata = useMemo((): CaptionTrackMetadata => {
    const preset = getCaptionPreset(presetName);
    const captionStyles = { ...preset, font_size: getPreviewFontSize(preset) } as CaptionStyles;
    const totalDuration = dummyWords.length * WORD_DURATION_SECONDS;

    return {
      segmentWiseData: [
        {
          segmentId: 'composite-segment',
          segmentType: SegmentType.COMPOSITE,
          captions: dummyWords,
          offset: 0
        }
      ],
      captionStyles,
      lineConfig: captionStyles.line_config || CaptionLineConfig.OneLine,
      captionPosition: getPreviewCaptionPosition(preset),
      size: 'LANDSCAPE' as SizeType,
      layout: 'DEFAULT',
      clipStart: 0,
      clipEnd: totalDuration
    };
  }, [presetName, dummyWords]);

  const pages = useMemo(() => {
    const preset = getCaptionPreset(presetName);
    return generateCaptionPages({
      metadata,
      elementTracks: [],
      fps,
      fontFamily: getFontFamilyStyle('caption-preview', preset.font_location) || FONT_DEFAULT_VALUE.name
    });
  }, [metadata, fps, presetName]);

  const totalDurationFrames = useMemo(
    () => Math.ceil(dummyWords.length * WORD_DURATION_SECONDS * fps),
    [dummyWords, fps]
  );

  const progressPercentage = useMemo(
    () => Math.min((frame / totalDurationFrames) * 100, 100),
    [frame, totalDurationFrames]
  );

  const backgroundImage = useMemo(() => {
    return `url(${getEnvConfig('STATIC_ASSETS_CDN')}/content-lab/ui-assets/captions-gallery-bg-2.png)`;
  }, []);

  return (
    <AbsoluteFill style={{ position: 'relative', backgroundColor: 'oklch(86.9% 0.022 252.894)' }}>
      <div
        data-testid="background-overlay"
        style={{
          position: 'absolute',
          inset: 0,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.9,
          filter: 'blur(200px)',
          backgroundImage
        }}
      />
      <CaptionsPages
        pages={pages || []}
        canEditCaptions={false}
        drift={0}
        disableCaptionsDragging={true}
        disableCaptionsResize={true}
        onOutlineRelease={() => {}}
        onOutlineResize={() => {}}
      />
      <div
        data-testid="progress-bar-container"
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 16,
          width: '100%',
          borderRadius: 2,
          overflow: 'hidden',
          opacity: isHovered ? 1 : 0,
          transition: 'opacity 0.2s ease-in-out'
        }}
      >
        <div
          data-testid="progress-indicator"
          style={{
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            width: `${progressPercentage}%`,
            transition: 'width 0.1s ease-out',
            borderRadius: 2
          }}
        />
      </div>
    </AbsoluteFill>
  );
}

export default memo(CaptionPresetPreviewComposition);
